import { notification } from "antd";
import https from "../../base/utils/http";
import { httpService } from "../../base/services/httpService.service";
import { EntityTypeEnum } from "../../utils/enums";
import { isPlanDowngradesEnabled } from "../Billing/planDowngrades";

// Archiving keeps a workspace or a project, and everything in it, out of the
// plan's limits: hidden from everybody, its surveys collect no answers, never
// purged (unlike Deleted items), and back when restored — when the plan has
// room for it. An organization moving to a smaller plan archives
// what it doesn't need first (components/Billing/planChanges.js).
//
// The routes mirror the delete/restore ones (`toggleDeleteOrRestore`). No
// backend has them yet, so archiving waits behind the plan-change switch
// (components/Billing/planDowngrades.js).

/** Whether archiving is offered. */
export const isArchiveEnabled = () => isPlanDowngradesEnabled();

// PUT /DataCollection/{id}/toggleArchive. A refusal rejects with the server's
// answer: a restore the plan has no room for (409 PLAN_LIMIT_EXCEEDED), a
// project whose workspace is archived or deleted (400), no DeleteWorkspace or
// DeleteProject permission (403, a bare { message }), and "not found" (404;
// the backend before it answered a success with statusCode 204). The buttons
// are offered to the same roles as Delete.
const toggleArchive = (entityType, id, isArchived) =>
   https
      .put(
         `/DataCollection/${id}/toggleArchive?${new URLSearchParams({
            entityType,
            isArchived
         }).toString()}`
      )
      .then(({ data }) => {
         if (data?.isSuccess === false || (typeof data?.statusCode === "number" && data.statusCode !== 200)) {
            throw data;
         }
         return data;
      });

export const archiveApi = {
   archiveWorkspace: (id) => toggleArchive(EntityTypeEnum.Workspace, id, true),
   restoreWorkspace: (id) => toggleArchive(EntityTypeEnum.Workspace, id, false),
   archiveProject: (id) => toggleArchive(EntityTypeEnum.Project, id, true),
   restoreProject: (id) => toggleArchive(EntityTypeEnum.Project, id, false),

   /** The organization's archived workspaces. */
   archivedWorkspaces: () =>
      httpService
         .get("/Workspaces/getMyWorkspaces?isArchived=true")
         .then((answer) => (Array.isArray(answer?.data) ? answer.data : [])),

   /** A page of a workspace's archived projects: `{ projects, totalSize }`. */
   archivedProjects: (workspaceId, { page, pageSize }) =>
      httpService
         .get(
            `/Projects/getMyProjectsInWorkspace/${workspaceId}?${new URLSearchParams({
               isArchived: true,
               page,
               pageSize
            }).toString()}`
         )
         .then((answer) => ({
            projects: answer?.data?.projects ?? [],
            totalSize: answer?.data?.totalSize ?? 0
         }))
};

/**
 * What to say about a failed archive or restore: the server's words — a restore
 * over the plan's limit says what the plan holds — or the fallback.
 */
export const describeArchiveFailure = (failure, fallback) => {
   const body = failure?.response?.data ?? (typeof failure?.statusCode === "number" ? failure : null);
   return (
      (typeof body?.message === "string" && body.message) ||
      (Array.isArray(body?.errors) && body.errors.find(Boolean)) ||
      fallback
   );
};

const ARCHIVE = {
   [EntityTypeEnum.Workspace]: { label: "workspace", call: archiveApi.archiveWorkspace },
   [EntityTypeEnum.Project]: { label: "project", call: archiveApi.archiveProject }
};

/** Archive a workspace or a project and say how it went. Resolves whether it was archived. */
export async function archiveWithNotice(entityType, id) {
   const { label, call } = ARCHIVE[entityType];
   try {
      await call(id);
      notification.success({
         message: `${label === "workspace" ? "Workspace" : "Project"} archived successfully`,
         description: `It's hidden from everyone and no longer counts toward your plan. You can restore it from the archived ${label}s.`
      });
      return true;
   } catch (failure) {
      notification.error({
         message: "Oops! A problem has occurred!",
         description: describeArchiveFailure(
            failure,
            `We had a problem archiving the ${label}, please try again.`
         )
      });
      return false;
   }
}
